import { useRef, useState, useEffect } from 'react';
import './Transition.css';
import { motion } from 'framer-motion';
import { Copy } from 'lucide-react';

const Transition = () => {
  const snippetContainerRef = useRef(null);
  const pageSnippetRef = useRef(null);
  const previewRef = useRef(null);
  const [showScanning, setShowScanning] = useState(false);
  const [showPageScan, setShowPageScan] = useState(false);
  const [activeAnim, setActiveAnim] = useState('');
  const [pageActive, setPageActive] = useState(false);

  // 🔹 Snippets for the basic animation types
  const snippets = {
    fadeIn: `<div class="anim-type fadeIn">Fade In</div>`,
    slideUp: `<div class="anim-type slideUp">Slide Up</div>`,
    zoomIn: `<div class="anim-type zoomIn">Zoom In</div>`,
    flipIn: `<div class="anim-type flipIn">Flip In</div>`,
    bounceIn: `<div class="anim-type bounceIn">Bounce In</div>`,
  };

  // ✅ Copy snippet (shared logic)
  const copySnippet = (ref, setScanState) => {
    if (ref.current) {
      const range = document.createRange();
      range.selectNode(ref.current);
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(range);
      document.execCommand('copy');
      window.getSelection().removeAllRanges();

      setScanState(true);
      setTimeout(() => setScanState(false), 1000);
    }
  };

  // ✅ Apply animation preview dynamically
  useEffect(() => {
    const preview = previewRef.current;
    if (!preview || !activeAnim) return;
    preview.className = 'anim-type';
    void preview.offsetWidth;
    preview.classList.add(activeAnim);
  }, [activeAnim]);

  // ✅ Toggle Page Transition demo
  const togglePageTransition = () => {
    setPageActive(true);
    setTimeout(() => setPageActive(false), 1000);
  };

  return (
    <motion.div
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -10, opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className='carve-card' style={{ boxShadow: 'none' }}>
        <div className='carve-card-body'>

          {/* =====================================================
              SECTION 1 — Element Animations (fadeIn, slideUp, etc.)
          ===================================================== */}
          <div className="heading-xlarge">
            <u><b>transition</b></u>
          </div>

          <div className="transition-picker">
            {['fadeIn', 'slideUp', 'zoomIn', 'flipIn', 'bounceIn'].map(anim => (
              <div
                key={anim}
                className={`anim-trigger ${activeAnim === anim ? 'active' : ''}`}
                onClick={() => setActiveAnim(anim)}
              >
                {anim.replace(/([A-Z])/g, ' $1')}
              </div>
            ))}
          </div>

          <div className="transition-container">
            <div ref={previewRef} className='anim-type'>
              {activeAnim ? activeAnim.replace(/([A-Z])/g, ' $1') : 'Select an animation'}
            </div>
          </div>

          <div className={`center-container ${showScanning ? 'scanning' : ''}`}>
            <div className='snippet' ref={snippetContainerRef}>
              <pre>
                <code>
                  {activeAnim ? (
                    <span>
                      <span className="cn">&lt;div</span>
                      <span className="cln"> class="</span>
                      <span className="cn">anim-type {activeAnim}</span>
                      <span className="cln">"&gt;</span>
                      {activeAnim.replace(/([A-Z])/g, ' $1')}
                      <span className="cn">&lt;/div&gt;</span>
                    </span>
                  ) : (
                    <span>Select an animation to view snippet</span>
                  )}
                </code>
              </pre>
            </div>
            <button
              aria-label="Copy Button"
              onClick={() => copySnippet(snippetContainerRef, setShowScanning)}
            >
              <Copy />
            </button>
          </div>

          {/* =====================================================
              SECTION 2 — Page Transition Animation
          ===================================================== */}
          <div className="heading-xlarge" style={{ marginTop: '40px' }}>
            <u><b>page transition</b></u>
          </div>

          <div className="page-transition-demo">
            <div
              className={`carve-page-transition ${pageActive ? 'carve-page-transition-exit' : ''}`}
            >
              <div className="page-box">Page Transition Demo</div>
            </div>
            <button className="anim-trigger page-trigger" onClick={togglePageTransition}>
              Trigger Page Transition
            </button>
          </div>

          <div className={`center-container ${showPageScan ? 'scanning' : ''}`}>
            <div className='snippet' ref={pageSnippetRef}>
              <pre>
                <code>
                  <span className="cn">&lt;div</span>
                  <span className="cln"> class="</span>
                  <span className="cn">carve-page-transition</span>
                  <span className="cln">"&gt;</span><br />
                  &nbsp;&nbsp;Page Transition Content<br />
                  <span className="cn">&lt;/div&gt;</span><br /><br />

                  <span className="cn">/* Note:Optional Exit Class */</span><br />
                  <span className="cn">.carve-page-transition-exit</span> → fade out + slide left<br />
                </code>
              </pre>
            </div>
            <button
              aria-label="Copy Page Snippet"
              onClick={() => copySnippet(pageSnippetRef, setShowPageScan)}
            >
              <Copy />
            </button>
          </div>

        </div>
      </div>
    </motion.div>
  );
};

export default Transition;
